import React, { useEffect, useState, useCallback, Suspense, lazy } from 'react'
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import { GrowthBookProvider } from '@growthbook/growthbook-react'
import { growthbook } from '../../growthbook'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
    useAuthStore,
    useCCExpireStore,
    useErrorStore,
    useLoadingStore,
    useSuccessStore,
} from '../state/store'
import LoadingModal from '../components/LoadingModal/LoadingModal'
import ErrorModal from '../components/ErrorModal/ErrorModal'
import { PageErrorBoundary } from '../components/ErrorBoundary'
import { GoogleOAuthProvider } from '@react-oauth/google'
import SuccessModal from '../components/SuccessModal/SuccessModal'
import PageRoot from '../components/PageRoot/PageRoot'
import PageHeader from '../components/PageHeader/PageHeader'
import Nav from '../components/Nav/Nav'
import { ThemeProvider } from 'styled-components'
import { wisesheetsTheme } from '../utils/theme'
import {
    GlobalStyles,
    PageLoadingContainer,
    PageLoadingSpinner,
    StyledNavSection,
} from './styles'
import { httpClient } from '../utils/http'

// Retry helper for lazy loading with exponential backoff
const lazyWithRetry = <T extends React.ComponentType<any>>(
    componentImport: () => Promise<{ default: T }>,
    componentName: string,
    maxRetries: number = 3
): Promise<{ default: T }> => {
    return new Promise((resolve, reject) => {
        const attemptImport = (retryCount: number) => {
            componentImport()
                .then(resolve)
                .catch((error) => {
                    if (retryCount < maxRetries) {
                        const delay = Math.min(
                            1000 * Math.pow(2, retryCount),
                            5000
                        )
                        console.warn(
                            `Failed to load ${componentName} (attempt ${
                                retryCount + 1
                            }/${maxRetries + 1}). Retrying in ${delay}ms...`,
                            error
                        )
                        setTimeout(() => attemptImport(retryCount + 1), delay)
                    } else {
                        console.error(
                            `Failed to load ${componentName} after ${
                                maxRetries + 1
                            } attempts:`,
                            error
                        )
                        reject(error)
                    }
                })
        }
        attemptImport(0)
    })
}

// Lazy loaded page components for code splitting with retry logic
// This reduces initial bundle size and improves load time
// Retries up to 3 times with exponential backoff on failure
const Login = lazy(() =>
    lazyWithRetry(() => import('../pages/Login/Login'), 'Login')
)

const StatementDump = lazy(() =>
    lazyWithRetry(
        () => import('../pages/StatementDump/StatementDump'),
        'StatementDump'
    )
)

const Templates = lazy(() =>
    lazyWithRetry(() => import('../pages/Templates/Templates'), 'Templates')
)

const PasswordlessLogin = lazy(() =>
    lazyWithRetry(
        () => import('../pages/PasswordlessLogin/PasswordlessLogin'),
        'PasswordlessLogin'
    )
)

const Sso = lazy(() => lazyWithRetry(() => import('../pages/Sso/Sso'), 'Sso'))

const CompanyProfile = lazy(() =>
    lazyWithRetry(
        () => import('../pages/CompanyProfile/CompanyProfile'),
        'CompanyProfile'
    )
)

const TrendingStocks = lazy(() =>
    lazyWithRetry(
        () => import('../pages/TrendingStocks/TrendingStocks'),
        'TrendingStocks'
    )
)

const Chat = lazy(() =>
    lazyWithRetry(
        () => import('../pages/SpreadsheetChat/SpreadsheetChat'),
        'Chat'
    )
)

// Named exports need special handling with React.lazy
const Screener = lazy(() =>
    lazyWithRetry(
        () =>
            import('../pages/Screener/Screener').then((module) => {
                // Add defensive check to ensure the export exists
                if (!module.Screener) {
                    console.error(
                        'Screener component not found in module:',
                        module
                    )
                    throw new Error('Screener component not found')
                }
                return { default: module.Screener }
            }),
        'Screener'
    )
)

const FunctionBuilder = lazy(() =>
    lazyWithRetry(
        () =>
            import('../pages/FunctionBuilder/FunctionBuilder').then(
                (module) => {
                    // Add defensive check to ensure the export exists
                    if (!module.FunctionBuilder) {
                        console.error(
                            'FunctionBuilder component not found in module:',
                            module
                        )
                        throw new Error('FunctionBuilder component not found')
                    }
                    return { default: module.FunctionBuilder }
                }
            ),
        'FunctionBuilder'
    )
)

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 2,
            staleTime: 5 * 60 * 1000, // 5 minutes
            refetchOnWindowFocus: false,
        },
    },
})

function usePageViews() {
    const location = useLocation()
    const [lastLocation, setLastLocation] = useState('')
    // Optimize selectors - only subscribe to needed fields
    const token = useAuthStore((state) => state.token)
    const setAuth = useAuthStore((state) => state.setAuth)
    const fmpApiKey = useAuthStore((state) => state.fmpApiKey)

    // Memoize the auth check function to prevent recreation on each render
    const checkAuthStatus = useCallback(async () => {
        if (!token) return

        try {
            const response: { data: { token: string } } = await httpClient.post(
                '/public/auth/validate-auth-status',
                {
                    token: token,
                    platform: 'EXCEL',
                },
                {
                    headers: { 'Content-Type': 'application/json' },
                }
            )
            if (response.data.token !== undefined) {
                window.localStorage.setItem('token', response.data.token)
                if (fmpApiKey !== null) {
                    window.localStorage.setItem('fmpApiKey', fmpApiKey)
                }
                setAuth(response.data.token)
            }
        } catch (e) {
            console.error('Auth status check failed:', e)
        }
    }, [token, fmpApiKey, setAuth])

    useEffect(() => {
        if (lastLocation !== location.pathname) {
            setLastLocation(location.pathname)
            // Call the API to validate auth/subscription status for all routes that are NOT /
            if (location.pathname !== '/') {
                checkAuthStatus()
            }
        }
    }, [location, lastLocation, checkAuthStatus])
}

// Loading fallback for page suspense
const PageLoadingFallback: React.FC = () => (
    <PageLoadingContainer>
        <PageLoadingSpinner />
        <span>Loading...</span>
    </PageLoadingContainer>
)

function AppContent() {
    // Optimize selectors - only subscribe to needed fields
    const currentUser = useAuthStore((state) => state.currentUser)
    const setSubscriptionCCStatus = useCCExpireStore(
        (state) => state.setSubscriptionCCStatus
    )
    const loading = useLoadingStore((state) => state.loading)
    const cancelOperation = useLoadingStore((state) => state.cancelOperation)
    const error = useErrorStore((state) => state.error)
    const setError = useErrorStore((state) => state.setError)
    const message = useSuccessStore((state) => state.message)
    const setSuccessMessage = useSuccessStore(
        (state) => state.setSuccessMessage
    )
    const navigate = useNavigate()
    const location = useLocation()

    // Check if we're on a feature route to show Nav/Header
    const isFeatureRoute = location.pathname.startsWith('/feature')

    usePageViews()

    useEffect(() => {
        // Handle deep linking for SSO redirects
        const route = window.location.href.split('#')[1]
        if (route) {
            navigate(route)
        }
    }, [navigate])

    useEffect(() => {
        // Load features asynchronously when the app renders
        growthbook.init({ streaming: true })
    }, [])

    useEffect(() => {
        if (currentUser.email === '') return

        growthbook.setAttributes({
            id: currentUser.userId,
            email: currentUser.email,
        })

        const fetchSubscriptionStatus = async () => {
            try {
                const response = await httpClient.get(
                    '/api/private/stripe/subscription-status'
                )
                setSubscriptionCCStatus(response.data.expiring)
            } catch (e) {
                console.error('Failed to fetch subscription status:', e)
            }
        }

        fetchSubscriptionStatus()
    }, [currentUser, setSubscriptionCCStatus])

    // Memoize callbacks for modal dismissal
    const handleErrorDismiss = useCallback(() => {
        setError({ hasError: false, errorMessage: '' })
    }, [setError])

    const handleSuccessDismiss = useCallback(() => {
        setSuccessMessage('')
    }, [setSuccessMessage])

    return (
        <GoogleOAuthProvider clientId="301997072637-p0ch4m908fo0971kj9i0l2jjdon8fh44.apps.googleusercontent.com">
            <QueryClientProvider client={queryClient}>
                <LoadingModal
                    message={loading.message}
                    isShown={loading.isLoading}
                    progress={loading.progress}
                    onCancel={loading.progress ? cancelOperation : undefined}
                />
                <ErrorModal
                    message={error.errorMessage}
                    onDismissClick={handleErrorDismiss}
                    isShown={error.hasError}
                />
                <SuccessModal
                    message={message}
                    onDismissClick={handleSuccessDismiss}
                />
                {isFeatureRoute ? (
                    <PageRoot>
                        <StyledNavSection>
                            <PageHeader />
                            <Nav />
                        </StyledNavSection>
                        <Suspense fallback={<PageLoadingFallback />}>
                            <Routes>
                                <Route
                                    path="/feature/templates"
                                    element={
                                        <PageErrorBoundary pageName="Templates">
                                            <Templates />
                                        </PageErrorBoundary>
                                    }
                                />
                                <Route
                                    path="/feature/screener"
                                    element={
                                        <PageErrorBoundary pageName="Screener">
                                            <Screener />
                                        </PageErrorBoundary>
                                    }
                                />
                                <Route
                                    path="/feature/function-builder"
                                    element={
                                        <PageErrorBoundary pageName="FunctionBuilder">
                                            <FunctionBuilder />
                                        </PageErrorBoundary>
                                    }
                                />
                                <Route
                                    path="/feature/statement-dump"
                                    element={
                                        <PageErrorBoundary pageName="StatementDump">
                                            <StatementDump />
                                        </PageErrorBoundary>
                                    }
                                />
                                <Route
                                    path="/feature/company-profile/:symbol"
                                    element={
                                        <PageErrorBoundary pageName="CompanyProfile">
                                            <CompanyProfile />
                                        </PageErrorBoundary>
                                    }
                                />
                                <Route
                                    path="/feature/trending-stocks"
                                    element={
                                        <PageErrorBoundary pageName="TrendingStocks">
                                            <TrendingStocks />
                                        </PageErrorBoundary>
                                    }
                                />
                                <Route
                                    path="/feature/chat"
                                    element={
                                        <PageErrorBoundary pageName="Chat">
                                            <Chat />
                                        </PageErrorBoundary>
                                    }
                                />
                            </Routes>
                        </Suspense>
                    </PageRoot>
                ) : (
                    <Suspense fallback={<PageLoadingFallback />}>
                        <Routes>
                            <Route
                                path="/passwordless-login"
                                element={
                                    <PageErrorBoundary pageName="PasswordlessLogin">
                                        <PasswordlessLogin />
                                    </PageErrorBoundary>
                                }
                            />
                            <Route
                                path="/sso"
                                element={
                                    <PageErrorBoundary pageName="Sso">
                                        <Sso />
                                    </PageErrorBoundary>
                                }
                            />
                            <Route
                                path="/"
                                element={
                                    <PageErrorBoundary pageName="Login">
                                        <Login />
                                    </PageErrorBoundary>
                                }
                            />
                        </Routes>
                    </Suspense>
                )}
            </QueryClientProvider>
        </GoogleOAuthProvider>
    )
}

export default function App() {
    return (
        <ThemeProvider theme={wisesheetsTheme}>
            <GlobalStyles />
            <GrowthBookProvider growthbook={growthbook}>
                <AppContent />
            </GrowthBookProvider>
        </ThemeProvider>
    )
}
